import classnames from 'classnames/bind'
import { useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import { useNavBannerContext } from '~/components/Navigation/NavigationBanner'
import Image, { ImageProps } from '~/components/UI/Image'
import InlineCta, { InlineCtaProps } from '~/components/UI/InlineCta'

import { useStyle } from '~/providers/StyleProvider'

import { CTA, FILTERED_PAGE_HEADER_TITLES } from '~/data/dictionary'

import css from './styles.module.scss'

const IMAGES_SIZES = [{ breakpoint: 'md', ratio: 1 / 3 }, { ratio: 1 }]
const cx = classnames.bind(css)

export type FilteredHeaderBlockProps = {
  className?: string
  origin?: string
  family?: string
  bio?: string
  bioTruncated?: string
  bioIsTruncated?: boolean
  bioImage?: ImageProps
  materials?: string[]
  shops?: InlineCtaProps[]
  responsibilities?: string[]
  colors?: string[]
}

function FilteredHeaderBlock({
  className,
  bio,
  bioTruncated,
  bioIsTruncated,
  bioImage,
  materials,
  origin,
  family,
  shops,
  responsibilities,
  colors,
}: FilteredHeaderBlockProps) {
  const [isVisible] = useNavBannerContext()

  const textStyle = useStyle({
    textPreset: GlobalTextPreset.Text14GroteskRegular,
  })

  const listElemStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskMedium,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const listElemSmallStyle = useStyle({
    textPreset: GlobalTextPreset.Text12GroteskRegular,
  })

  const t = useTranslate()

  const [expanded, setExpanded] = useState(false)

  return (
    <div
      className={cx(css.FilteredHeaderBlock, className, {
        isBannerVisible: isVisible,
      })}>
      {bioTruncated && (
        <div className={cx(css.text, textStyle)}>
          <div>
            {bioTruncated && (
              <div className={cx(css.bio)}>
                {bioIsTruncated
                  ? expanded
                    ? bio
                    : bioTruncated
                  : bioTruncated}
              </div>
            )}

            {!expanded && (bioImage || bioIsTruncated) && (
              <InlineCta
                className={css.readMore}
                textStyling={GlobalTextStyling.UpperCase}
                isDiv
                onClick={() => setExpanded(true)}>
                {t(CTA.READ_MORE)}
              </InlineCta>
            )}
          </div>

          {expanded && bioImage && (
            <>
              <Image
                className={cx(css.bioImage)}
                layout="responsive"
                objectFit="cover"
                asPlaceholder
                sizesFromBreakpoints={IMAGES_SIZES}
                {...bioImage}
              />
              <InlineCta
                className={css.readLess}
                textStyling={GlobalTextStyling.UpperCase}
                isDiv
                onClick={() => setExpanded(false)}>
                {t(CTA.READ_LESS)}
              </InlineCta>
            </>
          )}
        </div>
      )}

      {origin && (
        <div className={cx(css.origin, listElemStyle)}>
          <div>{t(FILTERED_PAGE_HEADER_TITLES.ORIGIN)}</div>
          <span>{origin}</span>
        </div>
      )}

      {family && (
        <div className={cx(css.origin, listElemStyle)}>
          <div>{t(FILTERED_PAGE_HEADER_TITLES.FAMILY)}</div>
          <span>{family}</span>
        </div>
      )}

      {shops?.length > 0 && (
        <div className={cx(css.shops, listElemStyle)}>
          <div>{t(FILTERED_PAGE_HEADER_TITLES.STORE)}</div>
          <div className={cx(css.elements)}>
            {shops?.map((shop, key) => (
              <InlineCta
                key={`shop_link_${key}`}
                textPreset={GlobalTextPreset.Cta12Grotesk}
                className={cx(css.shop)}
                {...shop}
              />
            ))}
          </div>
        </div>
      )}

      {materials?.length > 0 && (
        <div className={cx(css.materials)}>
          <div className={listElemStyle}>
            {t(FILTERED_PAGE_HEADER_TITLES.MATERIALS)}
          </div>
          <div className={cx(css.elements, listElemStyle)}>
            {materials?.join(', ')}
          </div>
        </div>
      )}

      {responsibilities?.length > 0 && (
        <div className={cx(css.responsibilities)}>
          <div className={cx(listElemStyle)}>
            {t(FILTERED_PAGE_HEADER_TITLES.RESPONSIBILITIES)}
          </div>
          <div className={cx(css.elements)}>
            {responsibilities.map((item, index) => (
              <div
                key={`item_responsibility_${index}`}
                className={cx(css.responsibility, listElemSmallStyle)}>
                {item}
              </div>
            ))}
          </div>
        </div>
      )}

      {colors?.length > 0 && (
        <div className={cx(css.responsibilities)}>
          <div className={cx(listElemStyle)}>
            {t(FILTERED_PAGE_HEADER_TITLES.COLORS)}
          </div>
          <div className={cx(css.elements)}>
            {colors.map((item, index) => (
              <div
                key={`item_colors_${index}`}
                className={cx(css.responsibility, listElemSmallStyle)}>
                {item}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default FilteredHeaderBlock
